import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useSiteSettings } from "@/hooks/useSiteSettings";

interface Props {
  /** Localised category name (e.g. "Engines"), shown in the heading. */
  category: string;
}

/**
 * Stock / availability information panel shown at the top of each category
 * page, before the product listing. The wording is written for this business
 * and the company name is pulled live from Site Settings, so renaming the
 * business updates this notice everywhere automatically.
 */
export default function AvailabilityNotice({ category }: Props) {
  const { t } = useTranslation();
  const { site } = useSiteSettings();

  return (
    <div className="overflow-hidden rounded-lg border border-brand-gold/30 bg-brand-gold/10 p-6 md:p-8">
      <div className="flex flex-col gap-5 md:flex-row md:items-start md:gap-7">
        <span className="flex h-12 w-12 shrink-0 items-center justify-center rounded-full bg-brand-dark text-brand-gold">
          <i className="ri-information-line text-2xl"></i>
        </span>
        <div className="min-w-0">
          <span className="text-xs font-semibold uppercase tracking-widest text-brand-gold">
            {t("availability.badge")}
          </span>
          <h2 className="mt-1 font-heading text-xl font-semibold uppercase text-brand-dark md:text-2xl">
            {t("availability.title", { category })}
          </h2>
          <p className="mt-3 text-sm leading-relaxed text-neutral-700">
            {t("availability.text", { siteName: site.site_name })}
          </p>
          <Link
            to="/contact-us"
            className="mt-5 inline-flex items-center gap-2 rounded-md bg-brand-gold px-6 py-2.5 text-xs font-semibold uppercase tracking-wide text-brand-dark transition hover:bg-brand-dark hover:text-white whitespace-nowrap"
          >
            <i className="ri-message-3-line"></i>
            {t("availability.cta")}
          </Link>
        </div>
      </div>
    </div>
  );
}